/**
 * Risk ▸ Dashboard, Monitoring & Reports, risk ayarları ve PDF metinleri (K7; ADR 0095). Adlar
 * ortağın belgelerindeki gibi İngilizce (FD §2 "Dashboard", "Monitoring & Reports"); Türkçe ikinci
 * dil.
 */

export const riskDashboardEn = {
  title: 'Risk Dashboard',
  description:
    'Risk assessments assigned to the vessels, acknowledgements, reviews due, operations awaiting authorisation, high residual risks and stopped work.',
  shipNote:
    'The vessel’s own risk picture: current company risk assessments, acknowledgements and today’s operations. Open an item to act on it.',
  officeNote:
    'Fleet view by default; choose a vessel to see only its figures. Vessel data is read only in the office.',
  error: 'The risk dashboard could not be loaded.',
  vessel: 'Vessel',
  fleet: 'All vessels (fleet)',
  yes: 'Yes',
  tiles: {
    assigned: 'Current Assessments',
    pending: 'Awaiting Acknowledgement',
    pendingLate: 'Awaiting Acknowledgement ({{late}} late)',
    reviews: 'Reviews Due / Overdue',
    awaiting: 'Awaiting Authorisation',
    open: 'Open Verifications',
    highRisk: 'High / Critical Residual Risk',
    stopped: 'Work Stopped',
    findings: 'Open Findings',
    findingsOverdue: 'Open Findings ({{overdue}} overdue)',
    compliance: 'Register Compliance',
  },
  library: {
    title: 'Company Library',
    published: 'Published',
    inReview: 'In review',
    approved: 'Approved, not published',
    draft: 'Draft or returned',
    reviewDue: 'Review due',
    reviewOverdue: 'Review overdue',
    highResidual: 'High / Critical residual',
    total: 'Assessments',
  },
  vessels: {
    title: 'Vessel Compliance',
    vessel: 'Vessel',
    select: 'Show {{name}}',
    assigned: 'Assigned',
    acknowledged: 'Acknowledged',
    pending: 'Awaiting',
    late_one: '{{count}} late',
    late_other: '{{count}} late',
    reviewOverdue: 'Review Overdue',
    outdated: 'On Old Version',
    open: 'Open Ops',
    awaiting: 'Awaiting Auth.',
    stopped: 'Stopped',
    compliance: 'Compliance',
  },
  attention: {
    authorizations: 'Awaiting Authorisation',
    noAuthorizations: 'No operation is waiting for authorisation.',
    stopped: 'Work Stopped',
    noStopped: 'No operation is stopped.',
    highRisk: 'Open Operations with High / Critical Residual Risk',
    noHighRisk: 'No open operation with a high residual risk.',
    acknowledgements: 'Awaiting Acknowledgement',
    noAcknowledgements: 'Every current version has been acknowledged.',
    reviews: 'Reviews Due',
    noReviews: 'No review is due.',
    more_one: 'and {{count}} more',
    more_other: 'and {{count}} more',
    stopReason: 'Stopped: {{reason}} ({{name}}, {{time}})',
    master: 'Master',
    since: 'expected since {{day}} ({{days}} d)',
    late: 'Late',
    awaiting: 'Awaiting',
  },
  review: { ok: 'In date', due: 'Due', overdue: 'Overdue' },
  acceptance: {
    acceptable: 'Acceptable',
    controls_required: 'Requires additional controls',
    escalation: 'Escalation required',
    unacceptable: 'Unacceptable',
  },
  settings: {
    title: 'Company Settings',
    text: 'Review at most {{months}} months after preparation; “due” {{due}} days before. Authorisation valid for at most {{hours}} hours. Acknowledgement late after {{ack}} days.',
  },
  settingsCard: {
    title: 'Risk Assessment',
    intro:
      'Review interval, warnings and authorisation validity for company risk assessments. Settings can only make the rules stricter: a review within 12 months and an authorisation of at most 24 hours stay the upper limits.',
    fields: {
      reviewMonths: 'Review interval',
      reviewDueDays: 'Review “due” warning',
      authorizationMaxHours: 'Authorisation validity',
      acknowledgementDueDays: 'Acknowledgement late after',
    },
    hints: {
      reviewMonths:
        'New assessments get a review date this many months after preparation; a later date cannot be submitted (1–12).',
      reviewDueDays: 'A version shows as “Due” this many days before its review date.',
      authorizationMaxHours:
        'An operation is authorised for at most this many hours (1–24); the planned end is the default.',
      acknowledgementDueDays:
        'A version not acknowledged on board within this many days of assignment shows as late in the office.',
    },
    units: { months: 'months', days: 'days', hours: 'hours' },
    readOnly: 'Set by the office; read only on board.',
    saved: 'Risk assessment settings saved.',
    save: 'Save',
    loadError: 'The risk assessment settings could not be loaded.',
    saveError: 'The settings could not be saved',
  },
  errors: {
    INVALID_RA_SETTINGS: 'A value is outside the allowed range.',
    ROW_VERSION_CONFLICT: 'The settings were changed by someone else. Reload and try again.',
    FORBIDDEN: 'You are not authorised to change these settings.',
    PDF_FAILED: 'The PDF could not be created.',
  },
  bell: {
    authorizations_one: '{{count}} operation awaiting authorisation',
    authorizations_other: '{{count}} operations awaiting authorisation',
  },
  reports: {
    title: 'Monitoring & Reports',
    description:
      'Status of the risk assessment library, acknowledgement and review compliance of the vessels and the history of operational verifications; CSV export.',
    shipNote:
      'Reports of this vessel. Open a row to see the record; the PDF is printed from the record.',
    officeNote: 'Fleet view by default; choose a vessel to narrow the vessel reports.',
    error: 'The reports could not be loaded.',
    kind: 'Report',
    kinds: {
      library: 'Library Status',
      compliance: 'Register Compliance',
      verifications: 'Verification History',
    },
    search: 'Search code, title, vessel or operation',
    status: 'Status',
    all: 'All',
    from: 'Opened from',
    to: 'Opened to',
    export: 'Export CSV',
    working: 'working copy {{revision}}',
    legacy: 'Legacy',
    openVersion: 'Open {{code}} in the register',
    openVerification: 'Open verification {{number}}',
    stopped: 'Stopped: {{reason}}',
    failed_one: '{{count}} No',
    failed_other: '{{count}} No',
    showing: '{{from}}–{{to}} of {{total}}',
    pagination: 'Report pages',
    empty: {
      title: 'Nothing to report yet',
      library: 'No company risk assessment has been created.',
      compliance: 'No company risk assessment is assigned to the vessel.',
      verifications: 'No operational verification in this period.',
      noMatch: 'No row matches the filters',
    },
    columns: {
      code: 'Assessment ID',
      title: 'Title',
      status: 'Status',
      workingRevision: 'Working Revision',
      publishedRevision: 'Published Rev.',
      publishedAt: 'Published',
      matrix: 'Matrix',
      rows: 'Risk Rows',
      residual: 'Residual Risk',
      acceptance: 'Acceptance',
      reviewDay: 'Review Due',
      review: 'Review State',
      assignedTo: 'Assigned To',
      vessel: 'Vessel',
      revision: 'Revision',
      acknowledgement: 'Acknowledgement',
      late: 'Late',
      acknowledgedBy: 'Acknowledged By',
      acknowledgedRole: 'Rank',
      acknowledgedAt: 'Acknowledged At',
      shipOffset: 'Ship Time Offset',
      assessments: 'Vessel RAs',
      number: 'Verification No',
      operation: 'Operation',
      location: 'Location',
      plannedStart: 'Planned Start',
      round: 'Round',
      answered: 'Answered',
      failed: '“No” Answers',
      authorizedBy: 'Authorised By',
      validUntil: 'Valid Until',
      stops: 'Stops',
      lastStop: 'Last Stop',
      createdBy: 'Opened By',
      createdAt: 'Opened At',
    },
  },
  pdf: {
    button: 'PDF',
    error: 'The PDF could not be created.',
    none: 'None.',
    printed: 'Printed {{at}}',
    page: 'Page {{page}} of {{pages}}',
    yes: 'Yes',
    no: 'No',
    legacy: 'Legacy 5-band',
    mandatory: 'mandatory',
    sameActor: 'same person as the submitter',
    trailOffice:
      'The approval trail is kept by the office; print from the office for the full trail.',
    unanswered: 'Not answered',
    validUntil: 'valid until {{at}}',
    band: 'residual band {{band}}',
    masterAcceptance: 'master’s acceptance of the residual risk',
    reauthenticated: 'password confirmed',
    version: {
      title: 'Risk Assessment',
      revision: 'Revision {{revision}}',
      published: 'Published',
      migrated: 'Migrated from template',
      general: 'General',
      publication: 'Publication',
      rows: 'Hazards, Controls and Risk',
      checks: 'Activity-Specific Operational Checks',
      trail: 'Approval Trail',
      acknowledgement: 'Acknowledgement on Board · {{vessel}}',
    },
    verification: {
      title: 'Operational Risk Verification',
      critical: 'Critical residual risk: work must not start',
      operation: 'Operation and Authorisation',
      authorizedBy: '{{name}}, valid until {{until}}',
      checklist: 'Checklist ({{answered}}/{{total}} answered)',
      history: 'Earlier Answers',
      events: 'Status Trail',
      briefed: 'Crew Briefing Acknowledged',
      note: 'Six “Yes” answers are not permission to start; only the recorded authorisation is. Times are ship local time (LT) with the UTC offset, and UTC.',
    },
    fields: {
      code: 'Assessment ID',
      revision: 'Revision',
      title: 'Title',
      activity: 'Activity',
      type: 'Assessment Type',
      groups: 'Vessel Types',
      location: 'Location / Context',
      team: 'Assessment Team',
      prepared: 'Prepared',
      review: 'Review Due',
      procedures: 'Procedures',
      scope: 'Scope',
      publishedAt: 'Published At',
      publishedBy: 'Published By',
      matrix: 'Risk Matrix',
      matrixVersion: 'Version {{version}}',
      hash: 'Content Hash (SHA-256)',
      acknowledgement: 'Acknowledgement',
      acknowledgedBy: 'Acknowledged By',
      acknowledgedAt: 'Acknowledged At',
      basis: 'Assigned By',
      number: 'Verification No',
      vessel: 'Vessel',
      operation: 'Operation',
      plannedStart: 'Planned Start',
      plannedEnd: 'Planned End',
      ra: 'Risk Assessment',
      round: 'Round',
      residual: 'Residual Risk',
      masterRequired: 'Master Authorises',
      checklistHash: 'Checklist Hash',
      raHash: 'Assessment Hash',
      openedBy: 'Opened By',
      authorization: 'Authorisation',
    },
    columns: {
      no: '#',
      step: 'Task Step',
      hazard: 'Hazard',
      consequence: 'Consequence',
      existing: 'Existing Controls',
      initial: 'Initial Risk',
      additional: 'Additional Controls',
      residual: 'Target Residual',
      check: 'Check',
      na: 'N/A Allowed',
      at: 'Time',
      event: 'Event',
      by: 'By',
      details: 'Details',
      answer: 'Answer',
      comment: 'Reason / Comment',
      evidence: 'Evidence',
      round: 'Round',
    },
  },
};

export const riskDashboardTr: typeof riskDashboardEn = {
  title: 'Risk Panosu',
  description:
    'Gemilere atanmış risk değerlendirmeleri, okundu onayları, gözden geçirmesi gelenler, yetki bekleyen operasyonlar, yüksek kalan riskler ve durdurulan işler.',
  shipNote:
    'Geminin risk tablosu: güncel şirket risk değerlendirmeleri, okundu onayları ve günün operasyonları. İşlem için maddeyi açın.',
  officeNote:
    'Varsayılan filo görünümüdür; yalnız bir geminin sayıları için gemi seçin. Gemi verisi ofiste salt okunurdur.',
  error: 'Risk panosu yüklenemedi.',
  vessel: 'Gemi',
  fleet: 'Bütün gemiler (filo)',
  yes: 'Evet',
  tiles: {
    assigned: 'Güncel Değerlendirmeler',
    pending: 'Onay Bekleyen',
    pendingLate: 'Onay Bekleyen ({{late}} gecikmiş)',
    reviews: 'Gözden Geçirmesi Gelen / Geçen',
    awaiting: 'Yetki Bekleyen',
    open: 'Açık Doğrulamalar',
    highRisk: 'High / Critical Kalan Risk',
    stopped: 'Durdurulan İşler',
    findings: 'Açık Bulgular',
    findingsOverdue: 'Açık Bulgular ({{overdue}} gecikmiş)',
    compliance: 'Register Uyumu',
  },
  library: {
    title: 'Şirket Kütüphanesi',
    published: 'Yayımlı',
    inReview: 'İncelemede',
    approved: 'Onaylı, yayımlanmamış',
    draft: 'Taslak ya da iade',
    reviewDue: 'Gözden geçirmesi yaklaşan',
    reviewOverdue: 'Gözden geçirmesi geçen',
    highResidual: 'High / Critical kalan risk',
    total: 'Değerlendirme',
  },
  vessels: {
    title: 'Gemi Uyumu',
    vessel: 'Gemi',
    select: '{{name}} gemisini göster',
    assigned: 'Atanmış',
    acknowledged: 'Onaylanan',
    pending: 'Bekleyen',
    late_one: '{{count}} gecikmiş',
    late_other: '{{count}} gecikmiş',
    reviewOverdue: 'Gözden Geçirmesi Geçen',
    outdated: 'Eski Sürümde',
    open: 'Açık Op.',
    awaiting: 'Yetki Bekleyen',
    stopped: 'Durdurulan',
    compliance: 'Uyum',
  },
  attention: {
    authorizations: 'Yetki Bekleyenler',
    noAuthorizations: 'Yetki bekleyen operasyon yok.',
    stopped: 'Durdurulan İşler',
    noStopped: 'Durdurulmuş operasyon yok.',
    highRisk: 'High / Critical Kalan Riskli Açık Operasyonlar',
    noHighRisk: 'Kalan riski yüksek açık operasyon yok.',
    acknowledgements: 'Onay Bekleyenler',
    noAcknowledgements: 'Bütün güncel sürümler onaylandı.',
    reviews: 'Gözden Geçirmesi Gelenler',
    noReviews: 'Gözden geçirmesi gelen yok.',
    more_one: 've {{count}} tane daha',
    more_other: 've {{count}} tane daha',
    stopReason: 'Durduruldu: {{reason}} ({{name}}, {{time}})',
    master: 'Kaptan',
    since: '{{day}} gününden beri bekleniyor ({{days}} g)',
    late: 'Gecikmiş',
    awaiting: 'Bekliyor',
  },
  review: { ok: 'Süresinde', due: 'Yaklaşıyor', overdue: 'Geçti' },
  acceptance: {
    acceptable: 'Kabul edilebilir',
    controls_required: 'Ek önlem gerekir',
    escalation: 'Yükseltme gerekir',
    unacceptable: 'Kabul edilemez',
  },
  settings: {
    title: 'Şirket Ayarları',
    text: 'Gözden geçirme hazırlamadan en çok {{months}} ay sonra; {{due}} gün kala “yaklaşıyor”. Yetki en çok {{hours}} saat geçerli. Okundu onayı {{ack}} gün sonra gecikmiş.',
  },
  settingsCard: {
    title: 'Risk Değerlendirmesi',
    intro:
      'Şirket risk değerlendirmelerinin gözden geçirme aralığı, uyarılar ve yetki geçerliliği. Ayarlar kuralları yalnız sıkılaştırır: 12 ay içinde gözden geçirme ve en çok 24 saatlik yetki üst sınır olarak kalır.',
    fields: {
      reviewMonths: 'Gözden geçirme aralığı',
      reviewDueDays: 'Gözden geçirme “yaklaşıyor” uyarısı',
      authorizationMaxHours: 'Yetki geçerliliği',
      acknowledgementDueDays: 'Okundu onayı gecikmesi',
    },
    hints: {
      reviewMonths:
        'Yeni değerlendirmenin gözden geçirme günü hazırlamadan bu kadar ay sonradır; daha geç gün gönderilemez (1–12).',
      reviewDueDays: 'Sürüm gözden geçirme gününe bu kadar gün kala “Yaklaşıyor” görünür.',
      authorizationMaxHours:
        'Operasyon en çok bu kadar saat için yetkilendirilir (1–24); varsayılan planlanan bitiştir.',
      acknowledgementDueDays:
        'Atandıktan sonra bu kadar gün içinde gemide onaylanmayan sürüm ofiste gecikmiş görünür.',
    },
    units: { months: 'ay', days: 'gün', hours: 'saat' },
    readOnly: 'Ofis belirler; gemide salt okunurdur.',
    saved: 'Risk değerlendirmesi ayarları kaydedildi.',
    save: 'Kaydet',
    loadError: 'Risk değerlendirmesi ayarları yüklenemedi.',
    saveError: 'Ayarlar kaydedilemedi',
  },
  errors: {
    INVALID_RA_SETTINGS: 'Bir değer izin verilen aralığın dışında.',
    ROW_VERSION_CONFLICT: 'Ayarları başka biri değiştirdi. Yeniden yükleyip tekrar deneyin.',
    FORBIDDEN: 'Bu ayarları değiştirme yetkiniz yok.',
    PDF_FAILED: 'PDF oluşturulamadı.',
  },
  bell: {
    authorizations_one: '{{count}} operasyon yetki bekliyor',
    authorizations_other: '{{count}} operasyon yetki bekliyor',
  },
  reports: {
    title: 'İzleme ve Raporlar',
    description:
      'Risk değerlendirmesi kütüphanesinin durumu, gemilerin onay ve gözden geçirme uyumu ve operasyonel doğrulama geçmişi; CSV dışa aktarma.',
    shipNote: 'Bu geminin raporları. Kaydı görmek için satırı açın; PDF kayıttan basılır.',
    officeNote: 'Varsayılan filo görünümüdür; gemi raporlarını daraltmak için gemi seçin.',
    error: 'Raporlar yüklenemedi.',
    kind: 'Rapor',
    kinds: {
      library: 'Kütüphane Durumu',
      compliance: 'Register Uyumu',
      verifications: 'Doğrulama Geçmişi',
    },
    search: 'Kimlik, başlık, gemi ya da operasyon ara',
    status: 'Durum',
    all: 'Hepsi',
    from: 'Açılış (başlangıç)',
    to: 'Açılış (bitiş)',
    export: 'CSV Dışa Aktar',
    working: 'çalışma kopyası {{revision}}',
    legacy: 'Eski',
    openVersion: '{{code}} sürümünü register’da aç',
    openVerification: '{{number}} doğrulamasını aç',
    stopped: 'Durduruldu: {{reason}}',
    failed_one: '{{count}} Hayır',
    failed_other: '{{count}} Hayır',
    showing: '{{from}}–{{to}} / {{total}}',
    pagination: 'Rapor sayfaları',
    empty: {
      title: 'Henüz raporlanacak kayıt yok',
      library: 'Şirket risk değerlendirmesi oluşturulmamış.',
      compliance: 'Gemiye atanmış şirket risk değerlendirmesi yok.',
      verifications: 'Bu dönemde operasyonel doğrulama yok.',
      noMatch: 'Süzgeçlere uyan satır yok',
    },
    columns: {
      code: 'Değerlendirme Kimliği',
      title: 'Başlık',
      status: 'Durum',
      workingRevision: 'Çalışılan Revizyon',
      publishedRevision: 'Yayımlı Rev.',
      publishedAt: 'Yayım',
      matrix: 'Matris',
      rows: 'Risk Satırı',
      residual: 'Kalan Risk',
      acceptance: 'Kabul',
      reviewDay: 'Gözden Geçirme',
      review: 'Gözden Geçirme Durumu',
      assignedTo: 'Atandığı',
      vessel: 'Gemi',
      revision: 'Revizyon',
      acknowledgement: 'Okundu Onayı',
      late: 'Gecikmiş',
      acknowledgedBy: 'Onaylayan',
      acknowledgedRole: 'Rütbe',
      acknowledgedAt: 'Onay Zamanı',
      shipOffset: 'Gemi Saati Farkı',
      assessments: 'Gemi RA',
      number: 'Doğrulama No',
      operation: 'Operasyon',
      location: 'Yer',
      plannedStart: 'Planlanan Başlangıç',
      round: 'Tur',
      answered: 'Cevaplanan',
      failed: '“Hayır” Cevap',
      authorizedBy: 'Yetki Veren',
      validUntil: 'Geçerlilik',
      stops: 'Durdurma',
      lastStop: 'Son Durdurma',
      createdBy: 'Açan',
      createdAt: 'Açılış',
    },
  },
  pdf: {
    button: 'PDF',
    error: 'PDF oluşturulamadı.',
    none: 'Yok.',
    printed: 'Basım {{at}}',
    page: 'Sayfa {{page}} / {{pages}}',
    yes: 'Evet',
    no: 'Hayır',
    legacy: 'Eski 5 bant',
    mandatory: 'zorunlu',
    sameActor: 'gönderenle aynı kişi',
    trailOffice: 'Onay izi ofiste tutulur; izin tamamı için ofisten basın.',
    unanswered: 'Cevaplanmadı',
    validUntil: '{{at}} tarihine kadar geçerli',
    band: 'kalan risk bandı {{band}}',
    masterAcceptance: 'kaptanın kalan risk kabulü',
    reauthenticated: 'parola doğrulandı',
    version: {
      title: 'Risk Değerlendirmesi',
      revision: 'Revizyon {{revision}}',
      published: 'Yayımlandı',
      migrated: 'Şablondan dönüştü',
      general: 'Genel',
      publication: 'Yayım',
      rows: 'Tehlikeler, Önlemler ve Risk',
      checks: 'Faaliyete Özel Operasyon Maddeleri',
      trail: 'Onay İzi',
      acknowledgement: 'Gemide Okundu Onayı · {{vessel}}',
    },
    verification: {
      title: 'Operasyonel Risk Doğrulaması',
      critical: 'Critical kalan risk: iş başlamamalı',
      operation: 'Operasyon ve Yetki',
      authorizedBy: '{{name}}, {{until}} tarihine kadar geçerli',
      checklist: 'Kontrol Listesi ({{answered}}/{{total}} cevaplı)',
      history: 'Önceki Cevaplar',
      events: 'Durum İzi',
      briefed: 'Brifingi Onaylayan Mürettebat',
      note: 'Altı “Evet” işe başlama izni değildir; izin yalnız kaydedilen yetkidir. Zamanlar gemi yerel saati (LT) ve UTC farkıyla, bir de UTC olarak verilir.',
    },
    fields: {
      code: 'Değerlendirme Kimliği',
      revision: 'Revizyon',
      title: 'Başlık',
      activity: 'Faaliyet',
      type: 'Değerlendirme Türü',
      groups: 'Gemi Tipleri',
      location: 'Yer / Bağlam',
      team: 'Değerlendirme Ekibi',
      prepared: 'Hazırlama',
      review: 'Gözden Geçirme',
      procedures: 'Prosedürler',
      scope: 'Kapsam',
      publishedAt: 'Yayım Zamanı',
      publishedBy: 'Yayımlayan',
      matrix: 'Risk Matrisi',
      matrixVersion: 'Sürüm {{version}}',
      hash: 'İçerik Hash’i (SHA-256)',
      acknowledgement: 'Okundu Onayı',
      acknowledgedBy: 'Onaylayan',
      acknowledgedAt: 'Onay Zamanı',
      basis: 'Atama Dayanağı',
      number: 'Doğrulama No',
      vessel: 'Gemi',
      operation: 'Operasyon',
      plannedStart: 'Planlanan Başlangıç',
      plannedEnd: 'Planlanan Bitiş',
      ra: 'Risk Değerlendirmesi',
      round: 'Tur',
      residual: 'Kalan Risk',
      masterRequired: 'Yetkiyi Kaptan Verir',
      checklistHash: 'Kontrol Listesi Hash’i',
      raHash: 'Değerlendirme Hash’i',
      openedBy: 'Açan',
      authorization: 'Yetki',
    },
    columns: {
      no: '#',
      step: 'İş Adımı',
      hazard: 'Tehlike',
      consequence: 'Sonuç',
      existing: 'Mevcut Önlemler',
      initial: 'İlk Risk',
      additional: 'Ek Önlemler',
      residual: 'Hedef Kalan Risk',
      check: 'Madde',
      na: 'N/A Verilebilir',
      at: 'Zaman',
      event: 'Olay',
      by: 'Kim',
      details: 'Ayrıntı',
      answer: 'Cevap',
      comment: 'Gerekçe / Yorum',
      evidence: 'Kanıt',
      round: 'Tur',
    },
  },
};
